/**
 * Home's Briefing view: the greeting, an inline Practice Assistant composer,
 * the runway, then the assistant's briefing on the practice. "Since
 * Yesterday" prose with entity names in medium weight, "Needs You Today"
 * from the recommendations, "On the Calendar" with the inline agenda,
 * "Coming Up" as dated rows, and a footnote on how the briefing is written.
 * Opening Home never waits on the model: cached text paints first, skeletons
 * only on a true first load.
 */
import dayjs from "dayjs";
import {
  ArrowUpRight,
  CalendarClock,
  FileClock,
  Sparkles,
  UserPlus,
  type LucideIcon
} from "lucide-react";
import { Fragment, useMemo, type FC, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";

import { Button, buttonVariants } from "@/components/ui/button";
import { IconTile } from "@/components/ui/icon-tile";
import { Skeleton } from "@/components/ui/skeleton";
import { Surface } from "@/components/surface";
import {
  rangeForView,
  useCalendarEvents,
  useFirmReminders,
  useMergedCalendar
} from "@/features/calendar/queries";
import { calendarPathFor } from "@/features/calendar/routes";
import { formatTimeShort, type CalendarEvent } from "@/features/calendar";
import { cn } from "@/lib/utils";

import { parseRefMarkdown } from "../format";
import { briefingTitle } from "../greeting";
import type { BriefingResponse, Recommendation } from "../types";
import { BriefingAgenda } from "./BriefingAgenda";
import { briefingLead, greetingLine, writtenMeta } from "./briefingCopy";
import { splitBriefing } from "./briefingMarkdown";
import { comingUpRows, daysAway } from "./comingUp";
import { EntityChip } from "./EntityChip";
import { NavArrows, PaneBar } from "./PaneBar";
import { KIND_ICON, type TriageItem } from "./triageModel";

export const NEEDS_YOU_MAX = 4;

const Section: FC<{
  title: string;
  aside?: ReactNode;
  children: ReactNode;
}> = ({ title, aside, children }) => (
  <section className="mt-7">
    <div className="mb-2.5 flex min-h-6 items-center gap-2">
      <h3 className="text-base font-medium text-fg">{title}</h3>
      <span className="flex-1" />
      {aside}
    </div>
    {children}
  </section>
);

const Meta: FC<{ children: ReactNode; "data-testid"?: string }> = ({
  children,
  ...rest
}) => (
  <span className="text-sm text-fg-3" data-testid={rest["data-testid"]}>
    {children}
  </span>
);

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? "" : "s"}`;

/** Briefing prose with its refs rendered as entity names. */
export const BriefingProse: FC<{
  text: string;
  className?: string;
  as?: "p" | "span";
}> = ({ text, className, as: Tag = "p" }) => (
  <Tag className={cn("text-md text-fg-2", className)}>
    {splitBriefing(text).map((seg, i) =>
      seg.type === "ref" ? (
        <EntityChip
          key={i}
          name={seg.label}
          href={seg.href}
          title={seg.title}
        />
      ) : seg.bold ? (
        <span key={i} className="font-medium text-fg">
          {seg.text}
        </span>
      ) : (
        <Fragment key={i}>{seg.text}</Fragment>
      )
    )}
  </Tag>
);

/** Match a recommendation to the triage item it is about, when there is one. */
const itemForRec = (
  rec: Recommendation,
  items: TriageItem[]
): TriageItem | null => {
  const href = parseRefMarkdown(rec.ref)?.href ?? null;
  const path = rec.action.type === "navigate" ? rec.action.path : null;
  return (
    items.find(
      (t) =>
        (href && (t.ref.includes(href) || t.path === href)) ||
        (path && t.path === path)
    ) ?? null
  );
};

const REVIEW_KINDS = new Set<TriageItem["kind"]>([
  "intake",
  "approval",
  "mention"
]);

/** The row's kind icon and what its button says it does. */
export const recPresentation = (
  rec: Recommendation,
  item: TriageItem | null
): { icon: LucideIcon; action: string } => {
  if (item) {
    return {
      icon: KIND_ICON[item.kind],
      action: REVIEW_KINDS.has(item.kind) ? "Review" : "Open"
    };
  }
  if (rec.action.type === "prompt") return { icon: Sparkles, action: "Ask" };
  const { path } = rec.action;
  if (/intake/i.test(path)) return { icon: UserPlus, action: "Review" };
  if (/reminder|calendar/i.test(path))
    return { icon: CalendarClock, action: "Open" };
  if (/\/cases\//.test(path)) return { icon: FileClock, action: "Open" };
  return { icon: Sparkles, action: "Open" };
};

const BriefingSkeleton: FC = () => (
  <div className="flex flex-col gap-3" data-testid="briefing-loading">
    <Skeleton className="h-6 w-65" />
    <Skeleton className="h-3.25 w-85" />
    <div className="h-4" />
    {[100, 96, 88, 60].map((w, i) => (
      <Skeleton key={i} className="h-3.5" style={{ width: `${w}%` }} />
    ))}
    <div className="h-4" />
    {[0, 1, 2].map((i) => (
      <Skeleton key={i} className="h-13 w-full rounded-xl" />
    ))}
  </div>
);

type Props = {
  firstName: string | null;
  uid: string | null;
  briefing: BriefingResponse | null;
  briefingLoading: boolean;
  recommendations: Recommendation[];
  items: TriageItem[];
  onOpenItem: (id: string) => void;
  onPrompt: (text: string) => void;
  canNext: boolean;
  onNext: () => void;
  trailing?: ReactNode;
  /** Replaces the default pane bar (Home's view bar). */
  bar?: ReactNode;
  /** The inline Practice Assistant composer under the greeting. */
  composer?: ReactNode;
  /** Suggestion chips under the composer. */
  suggestions?: ReactNode;
  /** The runway, under the composer. */
  runway?: ReactNode;
  /** Cards under the greeting (the evening time review and carry-over). */
  topCards?: ReactNode;
  now?: Date;
};

export const BriefingView: FC<Props> = ({
  firstName,
  uid,
  briefing,
  briefingLoading,
  recommendations,
  items,
  onOpenItem,
  onPrompt,
  canNext,
  onNext,
  trailing,
  bar,
  composer,
  suggestions,
  runway,
  topCards,
  now = new Date()
}) => {
  const navigate = useNavigate();
  const range = useMemo(() => rangeForView("day", now), [now]);
  const reminders = useFirmReminders(range, { assignee: uid });
  const synced = useCalendarEvents(range);
  const mine = useMemo(
    () => synced.events.filter((e) => e.ownerUid === uid),
    [synced.events, uid]
  );
  const events = useMergedCalendar(reminders.events, mine);

  const upcoming = useMemo(
    () =>
      events
        .filter((e) => !e.allDay && e.start.getTime() > now.getTime())
        .sort((a, b) => a.start.getTime() - b.start.getTime()),
    [events, now]
  );
  const firstMeeting = upcoming.find(
    (e) => e.clientName && e.kind === "APPOINTMENT"
  );
  const nextReminder = upcoming.find((e) => e.kind === "REMINDER");

  const steps = useMemo(
    () =>
      recommendations.slice(0, NEEDS_YOU_MAX).map((rec) => ({
        rec,
        item: itemForRec(rec, items)
      })),
    [recommendations, items]
  );

  const comingUp = useMemo(
    () => comingUpRows(briefing?.coming_up ?? [], now),
    [briefing?.coming_up, now]
  );
  const datedCount = comingUp.filter((r) => r.date).length;

  const openCalendar = (event?: CalendarEvent) =>
    navigate(calendarPathFor(event ? event.start : now, "day"));

  const runRec = (rec: Recommendation, item: TriageItem | null) => {
    if (item) onOpenItem(item.id);
    else if (rec.action.type === "prompt") onPrompt(rec.action.prompt);
    else navigate(rec.action.path);
  };

  const showSkeleton = briefingLoading && !briefing;
  const unavailable =
    briefing?.llm.status === "unavailable" && !briefing.paragraphs.length;
  const meta = writtenMeta(briefing);

  return (
    <div className="flex min-h-0 flex-1 flex-col" data-testid="briefing-view">
      {bar ?? (
        <PaneBar trailing={trailing}>
          <Sparkles className="size-3.5 text-fg-3" />
          <span className="text-base text-fg">{briefingTitle(now)}</span>
          <span className="truncate text-base text-fg-3">
            · {dayjs(now).format("dddd, MMMM D")}
          </span>
          <span className="flex-1" />
          <NavArrows
            onPrev={() => undefined}
            onNext={onNext}
            canPrev={false}
            canNext={canNext}
          />
        </PaneBar>
      )}
      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className="mx-auto max-w-170 px-8 pt-7 pb-10">
          {showSkeleton ? (
            <BriefingSkeleton />
          ) : (
            <>
              <h1 className="text-2xl font-medium text-fg">
                {greetingLine(firstName, now)}
              </h1>
              <p className="mt-1 text-md text-fg-3">
                {briefing && briefing.changes_count > 0
                  ? `${briefingLead(briefing)} Here's what matters.`
                  : "Here's what matters today."}
              </p>

              {composer ? (
                <div className="mt-4" data-testid="briefing-composer">
                  {composer}
                </div>
              ) : null}

              {suggestions ? (
                <div className="mt-3" data-testid="briefing-suggestions">
                  {suggestions}
                </div>
              ) : null}

              {runway ? <div className="mt-4">{runway}</div> : null}

              {topCards}

              <Section
                title="Since Yesterday"
                aside={
                  meta ? <Meta data-testid="briefing-meta">{meta}</Meta> : null
                }
              >
                {briefing?.paragraphs.length ? (
                  <div className="space-y-2">
                    {briefing.paragraphs.map((p, i) => (
                      <BriefingProse key={i} text={p} />
                    ))}
                  </div>
                ) : (
                  <p
                    className="text-base text-fg-3"
                    data-testid="briefing-empty"
                  >
                    {unavailable
                      ? "Your assistant couldn't write today's briefing. Your inbox on the left is up to date."
                      : "Nothing new since yesterday. Your inbox on the left is up to date."}
                  </p>
                )}
              </Section>

              {steps.length ? (
                <Section
                  title="Needs You Today"
                  aside={<Meta>{plural(steps.length, "item")}</Meta>}
                >
                  <Surface
                    className="divide-y divide-line-1"
                    data-testid="needs-you-today"
                  >
                    {steps.map(({ rec, item }) => {
                      const { icon, action } = recPresentation(rec, item);
                      return (
                        <button
                          key={rec.id}
                          type="button"
                          onClick={() => runRec(rec, item)}
                          data-testid="needs-you-row"
                          className="flex w-full min-w-0 items-center gap-3 px-3 py-2.5 text-left outline-none hover:bg-fill-1 focus-visible:bg-fill-1"
                        >
                          <IconTile
                            icon={icon}
                            className="size-7"
                            data-testid="needs-you-icon"
                          />
                          <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                            <span className="truncate text-base text-fg">
                              {item?.title ?? rec.title}
                            </span>
                            <span className="truncate text-sm text-fg-3">
                              {rec.reason}
                            </span>
                          </span>
                          <span
                            aria-hidden
                            className={buttonVariants({
                              variant: "secondary",
                              size: "xs"
                            })}
                            data-testid="needs-you-action"
                          >
                            {action}
                          </span>
                        </button>
                      );
                    })}
                  </Surface>
                </Section>
              ) : null}

              <Section
                title="On the Calendar"
                aside={
                  <Button
                    variant="ghost"
                    size="xs"
                    className="-mr-2"
                    onClick={() => openCalendar()}
                    data-testid="briefing-open-calendar"
                  >
                    Open Calendar
                    <ArrowUpRight className="text-fg-3" />
                  </Button>
                }
              >
                <p className="-mt-1.5 mb-3 text-sm text-fg-3">
                  {upcoming.length === 0
                    ? "Nothing more on your calendar today."
                    : `${upcoming.length} more on your calendar today.`}
                  {firstMeeting ? (
                    <>
                      {" "}
                      Your first client meeting is{" "}
                      <span className="font-medium text-fg">
                        {firstMeeting.title}
                      </span>{" "}
                      with{" "}
                      <EntityChip
                        name={firstMeeting.clientName ?? ""}
                        href={`/clients/${encodeURIComponent(
                          firstMeeting.clientId ?? ""
                        )}`}
                        title="Client"
                      />{" "}
                      at {formatTimeShort(firstMeeting.start)}
                      {nextReminder ? (
                        <>
                          , and{" "}
                          <span className="font-medium text-fg">
                            {nextReminder.title}
                          </span>
                          {nextReminder.clientName ? (
                            <>
                              {" "}
                              for{" "}
                              <EntityChip
                                name={nextReminder.clientName}
                                href={`/clients/${encodeURIComponent(
                                  nextReminder.clientId ?? ""
                                )}`}
                                title="Client"
                              />
                            </>
                          ) : null}{" "}
                          is due at {formatTimeShort(nextReminder.start)}.
                        </>
                      ) : (
                        "."
                      )}
                    </>
                  ) : null}
                </p>
                <BriefingAgenda
                  events={events}
                  now={now}
                  onOpen={openCalendar}
                />
              </Section>

              {comingUp.length ? (
                <Section
                  title="Coming Up"
                  aside={
                    datedCount ? (
                      <Meta>{plural(datedCount, "item")}</Meta>
                    ) : null
                  }
                >
                  <Surface
                    className="divide-y divide-line-1"
                    data-testid="coming-up"
                  >
                    {comingUp.map((row, i) => (
                      <div
                        key={i}
                        className="flex min-h-11 items-center gap-3 px-3 py-1.5"
                        data-testid="coming-up-row"
                      >
                        {row.date ? (
                          <>
                            <span className="flex w-11 shrink-0 flex-col">
                              <span className="text-xs text-fg-3">
                                {dayjs(row.date).format("ddd")}
                              </span>
                              <span className="text-base font-medium text-fg tabular-nums">
                                {dayjs(row.date).format("MMM D")}
                              </span>
                            </span>
                            <span className="h-6 w-px shrink-0 bg-line-2" />
                          </>
                        ) : null}
                        <BriefingProse
                          as="span"
                          text={row.text}
                          className="min-w-0 flex-1 truncate text-base text-fg"
                        />
                        {row.date ? (
                          <span className="shrink-0 text-sm text-fg-3">
                            {daysAway(row.date, now)}
                          </span>
                        ) : null}
                      </div>
                    ))}
                  </Surface>
                </Section>
              ) : null}

              <p className="mt-6 text-sm text-fg-4">
                Written when your practice changes, at most once an hour.
                Opening Home never waits on it.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
